import React, { useEffect, useState } from 'react';
import { useWebSocket } from '../context/WebSocketContext';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeContext';
import { subscribeQueue, restoreQueue, cancelQueue, retryQueue, dismissQueue, queueOverview } from '../utils/fightQueue';
import './FightQueueStatus.css';

const endedStates = ['DONE', 'FAILED', 'CANCELLED'];

function StatusMark({ success, attention }) {
  return <span className={`pb-queue-mark${success ? ' is-success' : attention ? ' is-attention' : ''}`} aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      {success ? <path d="m5 12 4 4 10-10" /> : attention ? <><path d="M12 5v8" /><circle cx="12" cy="18" r=".7" fill="currentColor" /></> : <><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5l3 2" /></>}
    </svg>
  </span>;
}

export default function FightQueueStatus() {
  const { fightStatus, lastSecond } = useWebSocket();
  const { user } = useUser();
  const { theme } = useTheme();
  const [items, setItems] = useState([]);
  const [overview, setOverview] = useState(null);
  useEffect(() => {
    const unsubscribe = subscribeQueue(setItems);
    return unsubscribe;
  }, []);
  useEffect(() => { restoreQueue(); }, [user]);
  useEffect(() => {
    if (fightStatus !== 20 && fightStatus !== 30) { setOverview(null); return; }
    let stopped = false, timer;
    const refresh = async () => {
      try { const r = await queueOverview(); if (!stopped) setOverview(r.data); } catch {}
      if (!stopped) timer = setTimeout(refresh, 4000 + Math.random() * 2000);
    };
    refresh();
    return () => { stopped = true; clearTimeout(timer); };
  }, [fightStatus]);
  const draining = (fightStatus === 20 || fightStatus === 30) && lastSecond <= 0;
  const activeItems = items.filter(item => !endedStates.includes(item.state));
  const item = activeItems[activeItems.length - 1] || items[items.length - 1];
  if (!item && !draining) return null;
  const upload = item ? item.kind === 'upload' : fightStatus === 20;
  const ended = item && endedStates.includes(item.state);
  const waiting = item && ['WAITING', 'RETRY'].includes(item.state);
  const attention = item?.state === 'ATTENTION';
  const success = item?.state === 'DONE';
  const reconnecting = item?.reconnecting && !ended;
  const ahead = Math.max(0, Number(item?.ahead ?? Math.max(0, (item?.position || 1) - 1)) || 0);
  const progress = item?.progress == null ? null : Math.max(0, Math.min(100, Number(item.progress) || 0));
  let title, caption;
  if (!item) {
    title = upload ? '等待上传完成' : '等待投票完成';
    caption = upload ? '本轮已截止，完成后自动进入投票' : '本轮已截止，完成后公布结果';
  } else if (reconnecting) {
    title = '正在恢复连接'; caption = '正在确认提交结果，请勿重复操作';
  } else if (success) {
    title = upload ? '上传成功' : '投票成功';
    caption = draining ? (fightStatus === 20 ? '等待其他选手上传完成' : '等待本轮投票完成') : '';
  } else if (attention) {
    title = upload ? '上传未完成' : '投票未完成'; caption = '请重试，或联系现场管理员';
  } else if (ended) {
    title = item.state === 'CANCELLED' ? '已取消排队' : '提交未完成'; caption = item.message || '';
  } else if (waiting) {
    title = upload ? '上传排队中' : '投票排队中'; caption = upload ? '轮到后自动上传' : '轮到后自动投票';
  } else if (item.state === 'CONNECTING') {
    title = '正在加入队列'; caption = '请稍等';
  } else {
    title = upload ? (progress === 100 ? '正在确认上传' : '正在上传') : '正在提交投票'; caption = '请保持页面打开';
  }
  const pending = overview ? (upload ? overview.uploadActive + overview.uploadWaiting : overview.voteActive + overview.voteWaiting) : null;
  const dismissEnded = () => items.filter(i => endedStates.includes(i.state)).forEach(i => dismissQueue(i.requestId));
  return <aside className="pb-queue-overlay" aria-label="提交进度">
    <section className="pb-queue-card" data-queue-state={item?.state || 'DRAINING'} style={{
      '--queue-bg': theme.cardBg,
      '--queue-text': theme.text,
      '--queue-muted': theme.textSecondary,
      '--queue-border': theme.border,
      '--queue-shadow': theme.cardShadow,
      '--queue-hover': theme.buttonHoverBg,
      '--queue-accent': theme.tabActiveColor,
    }}>
      <div role="status" aria-live="polite" aria-atomic="true">
        <div className="pb-queue-heading">
          <StatusMark success={success} attention={attention || (ended && !success)} />
          <h2 className="pb-queue-title">{title}</h2>
        </div>
        {waiting && !reconnecting && <div className="pb-queue-position" aria-label={`排队第 ${ahead + 1} 位，前面还有 ${ahead} 人`}>
          <span className="pb-queue-label">前面还有</span>
          <span className="pb-queue-number">{ahead}</span><span className="pb-queue-unit">人</span>
        </div>}
        {!item && pending > 0 && <div className="pb-queue-position">
          <span className="pb-queue-label">还有</span>
          <span className="pb-queue-number">{pending}</span><span className="pb-queue-unit">人待完成</span>
        </div>}
        {item && upload && !waiting && !ended && !attention && !reconnecting && progress != null && progress < 100 &&
          <progress className="pb-queue-progress" aria-label="图片传输进度" max="100" value={progress} />}
        {caption && <p className="pb-queue-caption">{caption}</p>}
      </div>
      {attention && <div className="pb-queue-actions">
        <button className="pb-queue-primary" onClick={() => retryQueue(item.requestId)}>{upload ? '重试上传' : '重试投票'}</button>
        <button className="pb-queue-secondary" onClick={() => cancelQueue(item.requestId)}>取消本次提交</button>
      </div>}
      {ended ? <button className="pb-queue-primary" onClick={dismissEnded}>知道了</button>
        : waiting ? <button className="pb-queue-secondary" onClick={() => cancelQueue(item.requestId)}>取消排队</button> : null}
    </section>
  </aside>;
}
